import { Card, CardBody } from "@chakra-ui/react";
import { useStytch } from "@stytch/react";
import { AuthFactorSetupProps } from "features/auth/AuthFactor";
import {
  getStytchErrorMessage,
  getStytchSessionDurationMinutes,
} from "features/auth/StytchUtils";
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { LoadingState } from "shared/components/empty/LoadingState";
import { useToast } from "shared/components/toast/useToast";

export const AuthFactorWebauthnSetup: React.FC<AuthFactorSetupProps> = ({
  onSuccess,
  onCancel,
  rememberDevice,
}) => {
  const { t } = useTranslation(["common", "auth"]);
  const stytchClient = useStytch();
  const { showErrorToast } = useToast();

  const didStartRegisterRef = useRef(false);

  useEffect(() => {
    const startRegister = async () => {
      try {
        // Delete any unverified registrations so user can start over
        const stytchUser = stytchClient.user.getSync()!;
        await Promise.all(
          stytchUser.webauthn_registrations
            .filter((reg) => !reg.verified)
            .map(
              ({ webauthn_registration_id }) =>
                // @ts-expect-error missing type definition
                // eslint-disable-next-line @typescript-eslint/no-unsafe-call
                stytchClient.user.deleteWebauthnRegistration(
                  webauthn_registration_id,
                ) as Promise<void>,
            ),
        );

        await stytchClient.webauthn.register({
          session_duration_minutes:
            getStytchSessionDurationMinutes(rememberDevice),
        });

        await stytchClient.webauthn.authenticate({
          session_duration_minutes:
            getStytchSessionDurationMinutes(rememberDevice),
        });
        onSuccess();
      } catch (e) {
        showErrorToast(getStytchErrorMessage(t, e));
        onCancel();
      }
    };
    if (didStartRegisterRef.current) {
      return;
    }
    didStartRegisterRef.current = true;
    void startRegister();
  }, [onCancel, onSuccess, rememberDevice, showErrorToast, stytchClient, t]);

  return (
    <Card width="md">
      <CardBody py="48px">
        <LoadingState />
      </CardBody>
    </Card>
  );
};
